import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild, inject, HostListener, TemplateRef } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { debounceTime, distinctUntilChanged, map, Observable } from 'rxjs';
import { ProductDataService } from '../../services/product-data.service';
import { Store } from '@ngxs/store';
import { Pagination, Repair, RepairFiltersResponse, RepairInfo, RepairResponse } from '../../models/response.model';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import { ROLES } from '../../../authentication/authRoles.constants';
import { ExportResponse } from '../../../../shared/services/export-to-excel.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { MatDialog } from '@angular/material/dialog';
import { ExportModalComponent, ExportModalData } from '../../../../shared/components/export-modal/export-modal.component';
import { ExportColumnDef } from '../../../../shared/models/export-column-def.model';

@Component({
  selector: 'app-repairs',
  styleUrls: ['./repairs.component.scss'],
  templateUrl: './repairs.component.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RepairsComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public store = inject(Store);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);
  public cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);

  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  repairsList: Repair[] = [];
  codeFilterList: string[] = [];
  typeFilterList: string[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  isSaveEnabled = false;
  isModified = false;
  rollPartNumberList: string[] = [];
  finishedPartNumberList: string[] = [];
  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 15, 25, 50];
  productSaveDisabled = true;
  private readonly editableKeys: (keyof Repair)[] = [
    'rmpartNumber',
    'rrpartNumber',
    'ropartNumber',
    'freeRepairPartNumber',
    'repairDemeritPartNumber',
    'adrrpartNumber',
    'adropartNumber'
  ];

  openExportModal(): void {
    const columns: ExportColumnDef[] = Object.entries(this.columnNames).map(([key, label]) => ({ key, label, selected: true }));
    const data: ExportModalData = {
      columns,
      dataFetcher: this.exportDataFetcher,
      fileNamePrefix: this.dealerName + '-Repairs-export',
      maxExportRows: 10000
    };
    this.dialog.open(ExportModalComponent, { data, width: '480px' });
  }

  tableConfig: TableConfig<Repair> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    columns: {}
  };
  dealerName = this.store.selectSnapshot(DealerState.currentDealer).name;
  columnNames: Record<string, string> = {};

  exportDataFetcher: () => Observable<ExportResponse> = () =>
    this.productDataService.getAllRepairs(this.filterFormBackend.value, 1, 10000).pipe(
      map((response: any) => ({
        data: (response.data.repairs as Repair[]).map((item) => ({
          code: item.code,
          type: item.type,
          construction: item.construction,
          repairSize: item.repairSize,
          rmpartNumber: item.rmpartNumber,
          rrpartNumber: item.rrpartNumber,
          ropartNumber: item.ropartNumber,
          zoneRepairPartNumber: item.zoneRepairPartNumber,
          freeRepairPartNumber: item.freeRepairPartNumber,
          repairDemeritPartNumber: item.repairDemeritPartNumber,
          adrrpartNumber: item.adrrpartNumber,
          adropartNumber: item.adropartNumber
        }))
      }))
    );

  fetcher: PaginatorFetcher<Repair> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    return this.productDataService.getAllRepairs({ ...this.filterFormBackend.value, ...filters }, filters.page, filters.limit).pipe(
      map((repairResponse: RepairResponse) => {
        this.repairsList = repairResponse.data.repairs;
        this.pagination = repairResponse.data.pagination;
        this.updateDisplayedRowsForm(this.repairsList);
        this.displayedRowsForm.markAsPristine();
        return {
          items: this.repairsList,
          count: this.pagination.totalRecords
        };
      })
    );
  };

  constructor() {
    this.filterForm = this.fb.group({
      codes: [''],
      types: ['']
    });
    this.filterFormBackend = this.fb.group({
      codes: [''],
      types: ['']
    });
    this.rollPartNumberList = this.store.selectSnapshot(ProductsState.products).rollPartNumbers;
    this.finishedPartNumberList = this.store.selectSnapshot(ProductsState.products).finishedPartNumbers;
  }

  ngOnInit(): void {
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    this.isSaveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.productDataService.getRepairFilterValues().subscribe((response: RepairFiltersResponse) => {
      this.codeFilterList = response.data.codes || [];
      this.typeFilterList = response.data.types || [];
    });
    this.setupFormListeners();
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.checkIfModified();
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.columnNames = {
        code: this.translationService.translate('codeText'),
        type: this.translationService.translate('typeColumnText'),
        construction: this.translationService.translate('casingConstructionText'),
        repairSize: this.translationService.translate('sizeText'),
        rmpartNumber: this.translationService.translate('rmPartNumText'),
        rrpartNumber: this.translationService.translate('rrPartNumText'),
        ropartNumber: this.translationService.translate('roPartNumText'),
        zoneRepairPartNumber: this.translationService.translate('zoneRepairPartNum'),
        freeRepairPartNumber: this.translationService.translate('freeRepairPartNum'),
        repairDemeritPartNumber: this.translationService.translate('repairDemeritPartNum'),
        adrrpartNumber: this.translationService.translate('adrrPartNum'),
        adropartNumber: this.translationService.translate('adroPartNum')
      };

      this.tableConfig.columns = {
        code: { cell: (elm: Repair) => elm.code, header: this.translationService.translate('codeText') },
        type: { cell: (elm: Repair) => elm.type, header: this.translationService.translate('typeColumnText') },
        casingConstruction: {
          cell: (elm: Repair) => elm.construction,
          header: this.translationService.translate('casingConstructionText')
        },
        size: { cell: (elm: Repair) => elm.repairSize, header: this.translationService.translate('sizeText') },
        rmpartNumber: { cell: 'rmpartNumber', header: this.translationService.translate('rmPartNumText') },
        rrpartNumber: { cell: 'rrpartNumber', header: this.translationService.translate('rrPartNumText') },
        ropartNumber: { cell: 'ropartNumber', header: this.translationService.translate('roPartNumText') },
        zoneRepairPartNumber: {
          cell: (elm: Repair) => elm.zoneRepairPartNumber,
          header: this.translationService.translate('zoneRepairPartNum')
        },
        freeRepairPartNumber: { cell: 'freeRepairPartNumber', header: this.translationService.translate('freeRepairPartNum') },
        repairDemeritPartNumber: { cell: 'repairDemeritPartNumber', header: this.translationService.translate('repairDemeritPartNum') },
        adrrpartNumber: { cell: 'adrrpartNumber', header: this.translationService.translate('adrrPartNum') },
        adropartNumber: { cell: 'adropartNumber', header: this.translationService.translate('adroPartNum') }
      };
      this.cdr.markForCheck();
    });
  }

  private setupFormListeners(): void {
    this.filterForm.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe(() => {
      this.generateFilterForm();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    });
  }

  checkIfModified(): boolean {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.repairsList.length; i++) {
      const original = this.repairsList[i] as any;
      const current = formArray.at(i).value;

      const isModified = this.editableKeys.some((key) => (current[key] ?? '') !== (original[key] ?? ''));
      if (isModified) {
        return true;
      }
    }
    return false;
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      codes: this.filterForm.get('codes')?.value || '',
      types: this.filterForm.get('types')?.value || ''
    });
  }

  resetFilters(): void {
    this.filterForm.reset();
  }

  createRowForm(row: Repair): FormGroup {
    return this.fb.group({
      repairPk: [row.repairPk],
      dealerFk: [row.dealerFk],
      repair: [row.repair],
      description: [row.description],
      code: [row.code],
      type: [row.type],
      construction: [row.construction],
      repairSize: [row.repairSize],
      rmpartNumber: [row.rmpartNumber ?? null],
      rrpartNumber: [row.rrpartNumber ?? null],
      ropartNumber: [row.ropartNumber ?? null],
      zoneRepairPartNumber: [row.zoneRepairPartNumber ?? null],
      freeRepairPartNumber: [row.freeRepairPartNumber ?? null],
      repairDemeritPartNumber: [row.repairDemeritPartNumber ?? null],
      adrrpartNumber: [row.adrrpartNumber ?? null],
      adropartNumber: [row.adropartNumber ?? null]
    });
  }

  public updateDisplayedRowsForm(rows: Repair[]) {
    const rowForms = rows.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  getControl(item: Repair, controlName: keyof Repair): FormControl {
    const index = this.repairsList.findIndex((row) => row.repairPk === item.repairPk);
    if (index === -1) {
      return new FormControl('');
    }
    return (this.displayedRowsForm.get('rows') as FormArray).at(index).get(controlName) as FormControl;
  }

  save(): void {
    if (!this.isSaveEnabled) {
      return;
    }

    const modifiedRows = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    for (let i = 0; i < this.repairsList.length; i++) {
      const original = this.repairsList[i];
      const current = formArray.at(i).value;

      const isModified = this.editableKeys.some((key) => current[key] !== original[key]);
      if (isModified) {
        modifiedRows.push({ ...original, ...current });
      }
    }
    if (modifiedRows.length > 0) {
      const payload: RepairInfo = {
        repairs: modifiedRows,
        pagination: this.pagination
      };
      this.productDataService.updateRepairs(payload).subscribe({
        next: (response) => {
          // Sync repairsList with current form values to reflect the saved state
          const formArray = this.displayedRowsForm.get('rows') as FormArray;
          for (let i = 0; i < this.repairsList.length; i++) {
            const currentFormValue = formArray.at(i).value;
            this.repairsList[i] = { ...this.repairsList[i], ...currentFormValue };
          }

          this.cdr.markForCheck();
          this.alertService.showAlert({
            type: AlertType.success,
            content: this.translationService.translate('camSaveSuccessText')
          } as Alert);
          if (response && response.data && response.data.repairs) {
            for (const updatedRow of response.data.repairs) {
              const idx = this.repairsList.findIndex((row) => row.repairPk === updatedRow.repairPk);
              if (idx !== -1) {
                this.repairsList[idx] = { ...this.repairsList[idx], ...updatedRow };
              }
            }
          }
          this.updateDisplayedRowsForm(this.repairsList);
          this.displayedRowsForm.markAsPristine();
          this.isModified = false;
        },
        error: () => {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      });
    } else {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
    }
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.updateDisplayedRowsForm(this.repairsList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = true;
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }
}
